import React, { useEffect, useState, useRef } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toast';

/**
 * TestDataTab Component
 * Handles test data management including download template, upload, and display
 * Features:
 * - Download excel template from S3
 * - Upload edited excel file
 * - Display test data in responsive table with sticky headers
 */
const TestDataTab = ({ testCaseId, testCaseName }) => {
  // State management
  const [testDataList, setTestDataList] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Dummy test data structure - represents columns and rows
//   const dummyTestData = {
//     columns: ['username', 'password', 'email', 'first_name', 'last_name', 'heart_rate', 'age'],
//     rows:  [
//       { username: 'user2@example.com', password: 'Pass@456', email: 'user2@example.com', first_name: 'User', last_name: 'Two', heart_rate: 72, age: 28 },
//     ]
// }
//   // Fetch test data on mount
  useEffect(() => {
    fetchTestData();
  }, [testCaseId]);

  /**
   * Fetch test data from API
   * API: get_test_data
   * Method: GET
   * Query params: test_case_id
   */
  const fetchTestData = async () => {
    if (!testCaseId) {
      setTestDataList({ columns: [], rows: [] });
      setIsLoadingData(false);
      return;
    }

    setIsLoadingData(true);
    setDataError(null);

    try {
      const response = await axiosInstance.get(`/test_case/list_test_data?test_case_id=${testCaseId}`);
      // axiosInstance already unwraps to response.data
      const payload = response?.data && (response.data.columns || response.data.rows)
        ? response.data
        : response;
      const columns = payload?.columns || payload?.data?.columns || [];
      const rows = payload?.rows || payload?.data?.rows || [];
      setTestDataList({ columns, rows, ...payload });
    } catch (error) {
      console.error('Error fetching test data:', error);
      setDataError('Failed to load test data');
      setTestDataList({ columns: [], rows: [] });
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Handle template download
   * API: test_data_template
   * Method: GET
   * Query params: test_case_id
   * Response: S3 path to download
   */
  const handleDownloadTemplate = async () => {
    // debugger;
    setIsDownloadingTemplate(true);
    try {
      const response = await axiosInstance.get(`/test_case/get_test_data_file?test_case_id=${testCaseId}`);
      console.log('Template download response:', response);
      const s3Path = response.test_data_file;
      window.open(s3Path, '_blank');

      // Dummy S3 path download
      const dummyS3Url = s3Path;
      window.open(dummyS3Url, '_blank');
      
      // Show success message
      showToast(`✓ Template downloaded: TestCase_${testCaseName}_template.xlsx`, 'success');
    } catch (error) {
            const errorMessage =
              error?.response?.data?.detail ||
              error?.response?.data?.message ||
              (typeof error?.response?.data === "string"
                ? error.response.data
                : null) ||
              error?.message ||
              "Unknown error";
      toast.error(`Failed to download template: ${errorMessage}`);
      // showToast(`Failed to download template: ${errorMessage.detail}`, 'error');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  /**
   * Handle file upload via file picker
   */
  const handleFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFileUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle drag and drop
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      await processFileUpload(file);
    }
  };

  /**
   * Process file upload with validation
   * API: upload_test_data
   * Method: POST
   * Query params: test_case_id
   * Payload: multipart form data with excel file
   */
  const processFileUpload = async (file) => {
    // Client-side validation
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    const validExtensions = ['.xlsx', '.xls'];
    const fileName = file.name;
    const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(file.type) || !validExtensions.includes(fileExtension)) {
      showToast('Please upload a valid Excel file (.xlsx or .xls)', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      showToast('File size must be less than 5MB', 'error');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // TODO: Replace with actual API call when ready
      const response = await axiosInstance.post(
        `/test_case/upload_test_data_file?test_case_id=${testCaseId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if(response?.status == 200){
      showToast(`✓ File uploaded successfully: ${fileName}`, 'success');
      await fetchTestData();
      }

      // Simulate API delay
      // await new Promise(resolve => setTimeout(resolve, 1500));

      // Show success message

      // Refresh test data list
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast(`Failed to upload file: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Show toast notification
   */
  const showToast = (message, type = 'info') => {
    // Create and dispatch custom event for toast
    console.log(`Toast: ${type} - ${message}`);
    const event = new CustomEvent('showToast', {
      detail: { message, type }
    });
    window.dispatchEvent(event);
  };

  /**
   * Create an empty row object with all columns initialized
   */
  const createEmptyRow = () => {
    const columns = testDataList?.columns || [];
    return columns.reduce((row, column) => {
      row[column] = '';
      return row;
    }, {});
  };

  /**
   * Enter edit mode for test data
   * Initialize edited rows with current data
   */
  const handleEditModeClick = () => {
    setIsEditMode(true);
    setValidationErrors({});
    setEditedRows(JSON.parse(JSON.stringify(testDataList.rows || [])));
  };

  /**
   * Cancel edit mode and discard changes
   */
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedRows([]);
    setValidationErrors({});
  };

  /**
   * Add a new empty row in edit mode
   */
  const handleAddRow = () => {
    setEditedRows((prev) => [...prev, createEmptyRow()]);
  };

  /**
   * Validate all fields in edited rows are filled
   */
  const validateEditedRows = () => {
    const columns = testDataList?.columns || [];
    const errors = {};

    if (!editedRows || editedRows.length === 0) {
      showToast('Add at least one test data row', 'error');
      return false;
    }

    editedRows.forEach((row, rowIndex) => {
      columns.forEach((column) => {
        const value = row[column];
        const isEmpty = value === undefined || value === null || String(value).trim() === '';
        if (isEmpty) {
          errors[`${rowIndex}-${column}`] = `${column.replace(/_/g, ' ')} is required`;
        }
      });
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      showToast('Please fill in all required fields', 'error');
      return false;
    }

    return true;
  };

  /**
   * Update a specific cell value in edit mode
   */
  const handleCellChange = (rowIndex, columnName, value) => {
    const updatedRows = [...editedRows];
    if (!updatedRows[rowIndex]) {
      updatedRows[rowIndex] = {};
    }
    updatedRows[rowIndex][columnName] = value;
    setEditedRows(updatedRows);

    const errorKey = `${rowIndex}-${columnName}`;
    if (validationErrors[errorKey] && String(value).trim() !== '') {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  /**
   * Save edited test data
   * API: test_case/update_test_data
   * Method: PUT
   * Payload: { columns: string[], rows: object[] }
   */
  const handleSaveEdit = async () => {
    if (!validateEditedRows()) {
      return;
    }

    setIsSavingEdit(true);

    try {
      const columns = testDataList?.columns || [];

      const sanitizedRows = editedRows.map((row) =>
        columns.reduce((sanitizedRow, column) => {
          sanitizedRow[column] = row[column] !== undefined && row[column] !== null
            ? String(row[column]).trim()
            : '';
          return sanitizedRow;
        }, {})
      );

      const payload = {
        columns,
        rows: sanitizedRows,
      };

      const response = await axiosInstance.put(
        `/test_case/update_test_data?test_case_id=${testCaseId}`,
        payload
      );

      if (response?.status === 200 || response?.status_code === 200 || response?.status === 'success') {
        setTestDataList({
          ...testDataList,
          rows: sanitizedRows,
        });
        setIsEditMode(false);
        setEditedRows([]);
        setValidationErrors({});
        toast.success('Test data updated successfully!');
      } else {
        throw new Error(response?.message || 'Failed to update test data');
      }
    } catch (error) {
      console.error('Error saving test data:', error);
      showToast(`Failed to update test data: ${error.message}`, 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Get table columns from data
  const columns = testDataList?.columns || [];
  const rows = testDataList?.rows || [];

  return (
    <div className="test-data-tab">
      {/* Download Template Section */}
      <div className="td-section">
        <div className="td-section__row">
          <div>
            <h4 className="td-section__title">Download Template</h4>
            <p className="td-section__hint">
              Download the test data template to fill in your test data
            </p>
          </div>
          <button
            onClick={handleDownloadTemplate}
            disabled={isDownloadingTemplate}
            className="td-btn td-btn--primary"
            style={{ opacity: isDownloadingTemplate ? 0.7 : 1 }}
          >
            {isDownloadingTemplate ? (
              <>
                <div className="td-spinner" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span>Download Template</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Upload Test Data Section */}
      <div className="td-section">
        <h4 className="td-section__title" style={{ marginBottom: 12 }}>Upload Test Data</h4>
        
        {/* Drag and Drop Area + File Picker */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`td-dropzone${dragActive ? ' td-dropzone--active' : ''}${isUploading ? ' td-dropzone--busy' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={isUploading}
          />
          
          {isUploading ? (
            <div className="td-dropzone__inner">
              <div className="td-spinner td-spinner--lg" />
              <p className="td-dropzone__accent">Uploading file...</p>
            </div>
          ) : (
            <div className="td-dropzone__inner">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="td-dropzone__icon">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <div>
                <p className="td-dropzone__title">
                  {dragActive ? 'Drop your file here' : 'Drag and drop your file here'}
                </p>
                <p className="td-dropzone__hint">or click to select a file</p>
              </div>
              <p className="td-dropzone__formats">Supported formats: .xlsx, .xls (Max 5MB)</p>
            </div>
          )}
        </div>
      </div>

      {/* Test Data List Section */}
      <div className="td-section">
        <div className="td-section__row" style={{ marginBottom: 12 }}>
          <h4 className="td-section__title" style={{ margin: 0 }}>Test Data List</h4>
          
          {/* Edit/Cancel/Save Buttons */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {isEditMode ? (
              <>
                <button
                  onClick={handleAddRow}
                  disabled={isSavingEdit}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSavingEdit ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    opacity: isSavingEdit ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  title="Add new row"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Add Row
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSavingEdit ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    opacity: isSavingEdit ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSavingEdit ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                      Save
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSavingEdit}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSavingEdit ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    opacity: isSavingEdit ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={handleEditModeClick}
                disabled={columns.length === 0}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: columns.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  opacity: columns.length === 0 ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
                title={columns.length === 0 ? 'No data to edit' : 'Edit test data'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                </svg>
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {isLoadingData ? (
          <div className="td-empty">
            <div className="td-spinner td-spinner--lg" />
            <p className="td-section__hint">Loading test data...</p>
          </div>
        ) : dataError ? (
          <div className="td-alert td-alert--error">
            <p>⚠️ {dataError}</p>
          </div>
        ) : columns.length === 0 ? (
          <div className="td-empty td-empty--bordered">
            <p className="td-section__hint" style={{ fontStyle: 'italic' }}>
              No test data available. Upload a file to get started.
            </p>
          </div>
        ) : (
          <div className="td-table-wrap">
            <table className="td-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column}>
                      {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(isEditMode ? editedRows : rows).map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'td-row--even' : 'td-row--odd'}>
                    {columns.map((column) => (
                      <td key={`${rowIndex}-${column}`}>
                        {isEditMode ? (
                          <div>
                            <input
                              type="text"
                              required
                              className={`td-input${validationErrors[`${rowIndex}-${column}`] ? ' td-input--error' : ''}`}
                              value={row[column] !== undefined && row[column] !== null ? String(row[column]) : ''}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              placeholder={`Enter ${column.replace(/_/g, ' ').toLowerCase()} *`}
                            />
                            {validationErrors[`${rowIndex}-${column}`] && (
                              <span className="td-field-error">
                                {validationErrors[`${rowIndex}-${column}`]}
                              </span>
                            )}
                          </div>
                        ) : (
                          row[column] !== undefined && row[column] !== null ? String(row[column]) : '—'
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestDataTab;
